import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
import { DrawerService } from '../services/drawer.service';
import { LoggedUserService } from '../services/logged-user.service';
import { MapService } from '../services/map.service';
import { ModalService } from '../services/modal.service';
import { SignalrService } from '../services/signalr.service';
import { TokenService } from '../services/token.service';

/**
 * Written when a tab extends the session, to tell the others. Its value is only a
 * timestamp to keep each write distinct, since `storage` does not fire on a no-op set.
 */
const SESSION_EXTENDED_KEY = 'sessionExtendedAt';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService implements OnDestroy {

  private apiUrl = environment.apiUrl + "auth";
  private storageListener = (event: StorageEvent) => this.onStorageChange(event);

  /**
   * Session bootstrap to run when another tab signs in, registered by AppComponent.
   * InactivityService depends on this service, so it cannot be injected here.
   */
  onSignedInElsewhere?: () => void;

  /**
   * Timer restart to run when another tab extends the session, registered by
   * InactivityService itself (same DI direction problem as above).
   */
  onSessionExtendedElsewhere?: () => void;

  constructor(
    private http: HttpClient,
    private router: Router,
    private drawerService: DrawerService,
    private loggerUserService: LoggedUserService,
    private tokenService: TokenService,
    private modalService: ModalService,
    private signalrService: SignalrService,
    private mapService: MapService) {
    window.addEventListener('storage', this.storageListener);
  }

  ngOnDestroy() {
    window.removeEventListener('storage', this.storageListener);
  }

  /**
   * Keeps the other tabs in step with the tab that signed in or out.
   *
   * Sign out is detected on `access_token` being removed; sign in is detected on
   * `userLoggedIn` turning true, which LoginComponent writes last, once the session
   * is fully loaded. A token refresh only rewrites `access_token`, so it matches
   * neither branch and leaves the other tabs alone.
   */
  private onStorageChange(event: StorageEvent) {
    if (event.storageArea !== localStorage) return;

    if (event.key === 'access_token' && !event.newValue) {
      this.signalrService.stopConnection().finally(() => {
        this.loggerUserService.removeLogedUser();
        this.mapService.destroy();
        this.drawerService.closeDrawer();
        // A session-expiry prompt open in this tab is about a session that no
        // longer exists, so it has to go with it.
        this.modalService.dismissAll();
        this.router.navigate(['/login']);
      });
      return;
    }

    // Signed in on another tab. A tab stranded on the login page adopts the new
    // session in place; a tab already inside the app reloads to start clean.
    if (event.key === 'userLoggedIn' && event.newValue === 'true') {
      if (this.router.url.startsWith('/login')) {
        this.onSignedInElsewhere?.();
        this.router.navigate(['/home']);
      } else {
        window.location.reload();
      }
      return;
    }

    // The user chose to stay signed in on another tab. Every tab counts down against
    // the same token, so they all raised the prompt: close it and restart the timer
    // on the refreshed expiry, otherwise this tab is left with no timer at all and
    // never prompts again.
    if (event.key === SESSION_EXTENDED_KEY && event.newValue) {
      this.modalService.dismissAll();
      this.onSessionExtendedElsewhere?.();
    }
  }

  /** Tells the other tabs the session was just extended. */
  broadcastSessionExtended() {
    localStorage.setItem(SESSION_EXTENDED_KEY, Date.now().toString());
  }

  authenticate(username: string, password: string) {
    const body = { user: username, password, remember: true};
    return this.http.post(`${this.apiUrl}/login`, body).pipe(
      tap((response: any) => {
        localStorage.setItem('access_token', response.accessToken);
        localStorage.setItem('refresh_token', response.refreshToken);
      })
    );
  }

  forgotPassword(data: { username: string, email: string }) {
    return this.http.post(`${this.apiUrl}/forgot-pass`, data);
  }

  validateUser(token: string, password: string, repeatedPassword: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/validate`, { token, password, repeatedPassword });
  }

  logout() {
    this.signalrService.stopConnection().finally(() => {
      this.http.post(`${this.apiUrl}/revoke-token`, { token : localStorage.getItem('access_token') }).subscribe();
      this.removeLogedUser();
      this.mapService.destroy();
      this.drawerService.closeDrawer();
      this.router.navigate(['/login']);
    });
  }

  removeLogedUser() {
    this.loggerUserService.removeLogedUser();
    localStorage.removeItem('access_token');
  }

  /** Observable wrapper over the shared, deduped refresh (see TokenService). */
  refreshToken(): Observable<any> {
    return from(this.tokenService.refreshAccessToken());
  }
}
